import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import listItems from 'flarum/common/helpers/listItems';
import username from 'flarum/common/helpers/username';
import humanTime from 'flarum/common/utils/humanTime';
import VoteGutter from './components/VoteGutter';

/**
 * Turn the discussion list row into a post.
 *
 * This ADDS to DiscussionListItem's ItemLists rather than replacing the
 * component:
 *
 *   byline    community mark · community · posted by · when
 *   main      core's title and info line, untouched
 *   actions   the vote pill, the comment count, share
 *
 * 🚨 The byline and the action strip are SIBLINGS of core's main view, not
 * children of it. `mainView()` is a `<Link>`, so a button nested inside it
 * would be a button inside an anchor — invalid HTML that browsers un-nest on
 * their own, moving the control somewhere nobody styled. There is also no
 * `mainItems` ItemList to add to; an earlier version of this file assumed
 * there was and rendered nothing at all.
 */
export default function decorateRow() {
  /*
   * 🚨 A module PATH, not an imported prototype.
   *
   * Core's `extend()` resolves a string through flarum.reg.onLoad, so it keeps
   * working when a component lives in an async chunk. Importing one that has
   * not been registered yet throws at boot, which takes down every page rather
   * than one component.
   */
  extend('flarum/forum/components/DiscussionListItem', 'contentItems', function (items) {
    const discussion = this.attrs.discussion;
    items.add('warrenByline', bylineView(discussion), 110);
    const preview = previewView(discussion);
    if (preview) items.add('warrenPreview', preview, 70);
    items.add('warrenActions', actionsView(discussion), 60);

    /*
     * 🚨 Core's author item is removed, and its BADGES are re-rendered below.
     *
     * Removing it on its own is the trap: core renders the discussion badges
     * inside the same item, so sticky, locked and every badge another
     * extension contributes would quietly stop appearing on the list, with
     * nothing to connect the loss to this line. The byline renders
     * `discussion.badges()` itself, which is the same ItemList all of those
     * extensions add to.
     */
    items.remove('author');
  });

  /*
   * 🚨 Declare what can change to the row's own SubtreeRetainer.
   *
   * DiscussionListItem freezes its subtree behind a retainer keyed on
   * `discussion.freshness`. A vote changes the score without touching
   * freshness, so without this the model updates and the DOM never follows —
   * which looks exactly like an arrow that does nothing.
   */
  extend('flarum/forum/components/DiscussionListItem', 'oninit', function () {
    this.subtree.check(() => this.attrs.discussion.warrenScore(), () => this.attrs.discussion.warrenUserVote());
  });
}
function bylineView(discussion) {
  const user = discussion.user();
  const tags = discussion.tags && discussion.tags();
  const tag = tags && tags.length ? tags[0] : null;
  const badges = discussion.badges().toArray();
  const mark = m("span", {
    className: "Warren-community-dot",
    style: tag && tag.color() ? {
      background: tag.color()
    } : null
  });
  return m("div", {
    className: "Warren-byline"
  }, tag ? m(Link, {
    className: "Warren-community",
    href: app.route.tag(tag)
  }, mark, tag.name()) : m("span", {
    className: "Warren-community"
  }, mark), m("span", {
    className: "Warren-byline-meta"
  }, app.translator.trans('ernestdefoe-warren.forum.row.posted_by', {
    author: user ? m(Link, {
      href: app.route.user(user)
    }, username(user)) : username(user)
  }), m("span", {
    className: "Warren-byline-sep"
  }, "\xB7"), humanTime(discussion.createdAt())), badges.length ? m("ul", {
    className: "DiscussionListItem-badges badges badges--packed"
  }, listItems(badges)) : null);
}

/**
 * The picture, or the first couple of lines, or nothing.
 *
 * A post with an image leads with it; a post without one shows the opening
 * words. Never both: a row carrying a picture AND three lines of prose is
 * taller than two rows that each carry one, and a feed is a list before it is
 * a gallery.
 */
function previewView(discussion) {
  const image = discussion.warrenImage && discussion.warrenImage();
  if (image) {
    return m(Link, {
      className: "Warren-media",
      href: app.route.discussion(discussion)
    }, m("img", {
      src: image,
      alt: "",
      loading: "lazy"
    }));
  }
  const excerpt = discussion.warrenExcerpt && discussion.warrenExcerpt();
  if (excerpt) {
    return m(Link, {
      className: "Warren-excerpt",
      href: app.route.discussion(discussion)
    }, excerpt);
  }
  return null;
}
function actionsView(discussion) {
  const count = discussion.commentCount() || 0;
  return m("div", {
    className: "Warren-actions"
  }, m(VoteGutter, {
    discussion: discussion
  }), m(Link, {
    className: "Warren-action",
    href: app.route.discussion(discussion)
  }, m(Icon, {
    name: "far fa-comment-alt"
  }), app.translator.trans('ernestdefoe-warren.forum.row.comments', {
    count
  })), m("button", {
    type: "button",
    className: "Warren-action",
    onclick: e => {
      e.preventDefault();
      e.stopPropagation();
      share(discussion);
    }
  }, m(Icon, {
    name: "fas fa-share"
  }), app.translator.trans('ernestdefoe-warren.forum.row.share')));
}

/**
 * 🚨 Copies the link, and SAYS SO.
 *
 * A Share button that opens nothing and shows nothing is the commonest kind of
 * dead control: built, worded, styled, and doing its job invisibly, so
 * everyone assumes it is broken. The alert is the feedback.
 */
function share(discussion) {
  const url = app.forum.attribute('baseUrl') + app.route.discussion(discussion);
  const done = () => app.alerts.show({
    type: 'success'
  }, app.translator.trans('ernestdefoe-warren.forum.row.share_copied'));
  if (navigator.clipboard) {
    navigator.clipboard.writeText(url).then(done, () => window.prompt('', url));
  } else {
    window.prompt('', url);
  }
}